<template>
  <div class="accounts-view">
    <h1 class="text-h4 mb-4">{{ t('accounts.title') }}</h1>
    
    <v-card>
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="busca"
              label="Buscar por endereço"
              density="comfortable"
              variant="outlined"
              clearable
              hide-details
              append-inner-icon="mdi-magnify"
              @click:append-inner="buscarConta"
              @keyup.enter="buscarConta"
            ></v-text-field>
          </v-col>
          
          <v-col cols="12" sm="3">
            <v-select
              v-model="filtroTipo"
              :items="opcoesTipos"
              label="Tipo de conta"
              density="comfortable"
              variant="outlined"
            ></v-select>
          </v-col>
          
          <v-col cols="12" sm="3">
            <v-select
              v-model="ordenarPor"
              :items="opcoesOrdenacao"
              label="Ordenar por"
              density="comfortable"
              variant="outlined"
            ></v-select>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    
    <v-skeleton-loader
      v-if="carregando"
      type="card"
      class="mt-4"
    ></v-skeleton-loader>
    
    <v-card
      v-else
      class="mt-4"
    >
      <v-data-table
        :headers="cabecalhos"
        :items="contas"
        :loading="carregando"
        hover
        class="accounts-table"
      >
        <!-- Endereço -->
        <template v-slot:item.address="{ item }">
          <div class="d-flex align-center">
            <v-avatar
              size="28"
              :color="isValidador(item.address) ? 'success' : 'secondary'"
              class="mr-2"
            >
              <v-icon size="small" color="white">
                {{ isValidador(item.address) ? 'mdi-shield-check' : 'mdi-account-outline' }}
              </v-icon>
            </v-avatar>
            <router-link 
              :to="{ name: 'account-details', params: { address: item.address } }"
              class="text-primary text-decoration-none text-truncate"
            >
              {{ item.address }}
            </router-link>
          </div>
        </template>
        
        <!-- Tipo -->
        <template v-slot:item.tipo="{ item }">
          <v-chip
            v-if="isValidador(item.address)"
            color="success"
            size="small"
            variant="outlined"
          >
            <v-icon start size="small">mdi-shield-check</v-icon>
            Validador
          </v-chip>
          <v-chip
            v-else
            color="secondary"
            size="small"
            variant="outlined"
          >
            <v-icon start size="small">mdi-account</v-icon>
            Usuário
          </v-chip>
        </template>
        
        <!-- Saldo -->
        <template v-slot:item.saldo="{ item }">
          <span class="font-weight-medium">{{ formatarValorToken(item.saldo) }}</span>
        </template>
        
        <!-- Transações -->
        <template v-slot:item.transacoes="{ item }">
          {{ item.transacoes.toLocaleString('pt-BR') }}
        </template>
        
        <!-- Última atividade -->
        <template v-slot:item.ultimaAtividade="{ item }">
          <v-tooltip location="top">
            <template v-slot:activator="{ props }">
              <span v-bind="props">{{ tempoRelativo(item.ultimaAtividade) }}</span>
            </template>
            {{ formatarData(item.ultimaAtividade) }}
          </v-tooltip>
        </template>
        
        <!-- Ações -->
        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-end">
            <v-btn
              icon
              variant="text"
              size="small"
              :to="{ name: 'account-details', params: { address: item.address } }"
            >
              <v-icon>mdi-eye-outline</v-icon>
            </v-btn>
            
            <v-btn
              icon
              variant="text"
              size="small"
              @click="copiarEndereco(item.address)"
            >
              <v-icon>mdi-content-copy</v-icon>
            </v-btn>
          </div>
        </template>
        
        <!-- Empty state -->
        <template v-slot:no-data>
          <div class="pa-4 text-center">
            <v-icon size="48" color="grey" class="mb-2">mdi-account-search-outline</v-icon>
            <div class="text-body-1">{{ t('accounts.noResults') }}</div>
          </div>
        </template>
      </v-data-table>
    </v-card>
    
    <v-pagination
      v-model="paginaAtual"
      :length="totalPaginas"
      :total-visible="7"
      class="mt-4"
    ></v-pagination>
    
    <!-- Snackbar para confirmação de cópia -->
    <v-snackbar
      v-model="snackbarVisible"
      :timeout="2000"
      color="success"
    >
      Endereço copiado para a área de transferência
    </v-snackbar>
  </div>
</template>

<script>
import { useBlockchainStore } from '@/stores/blockchain'
import { formatarData, formatarValorToken, tempoRelativo } from '@/services/utilidades'

export default {
  name: 'AccountsView',
  data() {
    return {
      carregando: true,
      contas: [],
      paginaAtual: 1,
      itensPorPagina: 10,
      totalContas: 0,
      filtroTipo: 'todos',
      busca: '',
      _searchTimer: null,
      ordenarPor: 'saldo_desc',
      snackbarVisible: false,
      opcoesTipos: [
        { title: 'Todas', value: 'todos' },
        { title: 'Contas', value: 'conta' },
        { title: 'Contratos', value: 'contrato' }
      ],
      opcoesOrdenacao: [
        { title: 'Saldo (maior)', value: 'saldo_desc' },
        { title: 'Saldo (menor)', value: 'saldo_asc' },
        { title: 'Transações (maior)', value: 'transacoes_desc' },
        { title: 'Transações (menor)', value: 'transacoes_asc' },
        { title: 'Atividade recente', value: 'atividade_desc' }
      ],
      cabecalhos: [
        { title: 'Endereço', key: 'address', align: 'start' },
        { title: 'Tipo', key: 'tipo', align: 'center' },
        { title: 'Saldo', key: 'saldo', align: 'end' },
        { title: 'Transações', key: 'transacoes', align: 'end' },
        { title: 'Última Atividade', key: 'ultimaAtividade', align: 'center' },
        { title: 'Ações', key: 'actions', align: 'end', sortable: false }
      ]
    }
  },
  computed: {
    blockchainStore() {
      return useBlockchainStore()
    },
    
    totalPaginas() {
      return Math.ceil(this.totalContas / this.itensPorPagina)
    }
  },
  methods: {
    formatarData,
    formatarValorToken,
    tempoRelativo,
    
    isValidador(address) {
      if (!address) return false
      return this.blockchainStore.validators.some(v => v.address === address)
    },
    
    async carregarContas() {
      this.carregando = true
      
      try {
        const { ExplorerAPI } = await import('@/services/api')
        
        // Buscar validadores como contas conhecidas
        const validadores = await ExplorerAPI.validadores.obterTodos()
        
        // Mapear validadores para formato de conta
        const contasDeValidadores = await Promise.all(
          validadores.map(async (v) => {
            const endereco = v.endereco || v.address || ''
            let saldo = v.stake || 0
            try {
              const balanceResp = await ExplorerAPI.contas.obterPorEndereco(endereco)
              if (balanceResp && balanceResp.balance !== undefined) {
                saldo = balanceResp.balance
              }
            } catch (_) {
              // Usa stake como fallback para saldo
            }
            return {
              address: endereco,
              tipo: 'conta',
              saldo,
              transacoes: v.blocosCriados || 0,
              ultimaAtividade: new Date(),
              tokens: 0
            }
          })
        )
        
        this.contas = contasDeValidadores
        
        // Ordenação
        this.ordenarContas()
        
        // Filtragem por tipo
        if (this.filtroTipo !== 'todos') {
          this.contas = this.contas.filter(conta => conta.tipo === this.filtroTipo)
        }
        
        this.totalContas = this.contas.length
      } catch (error) {
        console.error('Erro ao carregar contas:', error)
        this.contas = []
        this.totalContas = 0
      } finally {
        this.carregando = false
      }
    },
    
    ordenarContas() {
      const [campo, direcao] = this.ordenarPor.split('_')
      
      this.contas.sort((a, b) => {
        let comparacao = 0
        
        if (campo === 'saldo') {
          comparacao = a.saldo - b.saldo
        } else if (campo === 'transacoes') {
          comparacao = a.transacoes - b.transacoes
        } else if (campo === 'atividade') {
          comparacao = new Date(a.ultimaAtividade) - new Date(b.ultimaAtividade)
        }
        
        return direcao === 'desc' ? -comparacao : comparacao
      })
    },
    
    buscarConta() {
      if (this.busca && this.busca.length > 0) {
        // Se for um endereço completo, redireciona para a página de detalhes
        if (this.busca.startsWith('0x') && this.busca.length >= 40) {
          this.$router.push({ name: 'account-details', params: { address: this.busca } })
        } else {
          // Caso contrário, filtra a lista
          this.carregarContas()
        }
      } else {
        this.carregarContas() // Limpa o filtro
      }
    },
    
    copiarEndereco(endereco) {
      navigator.clipboard.writeText(endereco)
        .then(() => {
          this.snackbarVisible = true
        })
        .catch(err => {
          console.error('Erro ao copiar endereço:', err)
        })
    }
  },
  watch: {
    busca() {
      clearTimeout(this._searchTimer)
      this._searchTimer = setTimeout(() => {
        this.buscarConta()
      }, 300)
    },
    filtroTipo() {
      this.carregarContas()
    },
    ordenarPor() {
      this.ordenarContas()
    },
    paginaAtual() {
      window.scrollTo(0, 0)
      this.carregarContas()
    }
  },
  async mounted() {
    // Ensure validators are loaded for type classification
    if (this.blockchainStore.validators.length === 0) {
      this.blockchainStore.fetchValidators()
    }
    await this.carregarContas()
  }
}
</script>

<style scoped>
.accounts-view {
  padding-bottom: 32px;
}

.accounts-table .v-data-table__td {
  padding-top: 8px;
  padding-bottom: 8px;
}
</style>
